import type { TaskSortOption } from "@/components/features/matter/tasks/TaskQuickFilters";
import { useTasksViewModel } from "@/hooks/matter-view-models/use-tasks-view-model";
import { retrieveMassTortTasksInfiniteQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort-collections";
import type { Matter } from "@/lib/types";
import { getMassTortAnnotationTasks } from "@/lib/utils/massTortAnnotationUtils";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

export const useMassTortTasks = ({
  massTortId,
  matter,
  enabled,
  loadAll,
  sortOption,
}: {
  massTortId?: string;
  matter: Matter;
  enabled: boolean;
  loadAll: boolean;
  sortOption: TaskSortOption;
}) => {
  const query = useInfiniteQuery({
    ...retrieveMassTortTasksInfiniteQueryOptions({
      massTortId: massTortId ?? "",
    }),
    enabled: Boolean(massTortId) && enabled,
  });
  const mappedTasks = useMemo(
    () =>
      getMassTortAnnotationTasks(
        query.data?.pages.flatMap((page) => page.data) ?? [],
      ),
    [query.data?.pages],
  );
  const sortedTasks = useMemo(() => {
    const priorityRanks: Record<string, number> = {
      Low: 1,
      Medium: 2,
      High: 3,
      Urgent: 4,
    };
    const getValue = (task: (typeof mappedTasks)[number]) =>
      sortOption.startsWith("priority")
        ? priorityRanks[task.priority ?? ""]
        : sortOption.startsWith("dueDate")
          ? task.dueDate
          : task.dateCreated;
    const direction = sortOption.endsWith("Asc") ? 1 : -1;
    return [...mappedTasks].sort((first, second) => {
      const firstValue = getValue(first);
      const secondValue = getValue(second);
      // Keep missing due dates and priorities last in either direction, like matter tasks.
      if (firstValue == null || firstValue === "")
        return secondValue == null || secondValue === ""
          ? first.id.localeCompare(second.id)
          : 1;
      if (secondValue == null || secondValue === "") return -1;
      const comparison =
        firstValue < secondValue ? -1 : firstValue > secondValue ? 1 : 0;
      return comparison * direction || first.id.localeCompare(second.id);
    });
  }, [mappedTasks, sortOption]);
  const { allTasks: tasks } = useTasksViewModel(matter, sortedTasks);
  const { fetchNextPage, hasNextPage, isFetching, isError } = query;

  useEffect(() => {
    if (
      massTortId &&
      enabled &&
      loadAll &&
      hasNextPage &&
      !isFetching &&
      !isError
    ) {
      void fetchNextPage();
    }
  }, [
    massTortId,
    enabled,
    loadAll,
    hasNextPage,
    isFetching,
    isError,
    fetchNextPage,
    mappedTasks,
  ]);

  return { query, tasks };
};
